import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { OptionsType } from 'services/types'

const useScroll = (
  keyCursor: number | null,
  listRef: MutableRefObject<HTMLUListElement | null>,
  listItemsRefById: MutableRefObject<Record<string, HTMLLIElement | null>>,
  options: OptionsType[]
) => {
  const [elementOffset, setElementOffset] = useState(0)
  const keyCursorValueRef = useRef<number | null>(0)

  const hoveredElement = keyCursor !== null ? listItemsRefById.current?.[options[keyCursor]?._id] : null
  const heightOfElement = hoveredElement?.offsetHeight || 0

  // Store the previous value of keyCursor to see if we are scrolling up or down
  useEffect(() => {
    keyCursorValueRef.current = keyCursor
  }, [keyCursor])

  const isDown = !!(
    (keyCursorValueRef.current === null && keyCursor === 0) ||
    (keyCursorValueRef.current !== null && keyCursor !== null && keyCursorValueRef.current < keyCursor)
  )
  const isUp = keyCursorValueRef.current !== null && keyCursor !== null && !!(keyCursorValueRef.current > keyCursor)

  // Function to calculate the itemOffset
  useEffect(() => {
    setElementOffset(prevState => {
      if (!heightOfElement) return 0
      if (keyCursor === 0) return heightOfElement
      if (keyCursor === null) return 0
      if (isDown) return prevState + heightOfElement
      if (isUp) return prevState - heightOfElement
      return prevState
    })
  }, [heightOfElement, isDown, isUp, keyCursor, listRef])

  // Function to scroll in the UL to see the selected item
  // The choice of useLayoutEffect over useEffect deducted from these articles:
  // https://dev.to/dance2die/scrolling-with-page-up-down-keys-in-react-window-31ei
  // https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
  useLayoutEffect(() => {
    const heightOfUL = listRef.current?.offsetHeight || 0
    const scrollTop = listRef.current?.scrollTop || 0
    const viewPort = scrollTop + heightOfUL
    const targetOffSet = elementOffset - heightOfElement
    const shouldScroll = elementOffset > viewPort || targetOffSet < scrollTop
    if (shouldScroll) {
      listRef.current?.scrollTo({ left: 0, top: targetOffSet, behavior: 'smooth' })
    }
  }, [elementOffset, heightOfElement, listRef, keyCursor])
}

export default useScroll
